import { Ionicons } from "@expo/vector-icons";
import {
  WalletConnectWallet,
  WalletConnectChainID,
} from "@tronweb3/walletconnect-tron";
import { useFocusEffect } from "expo-router";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Pressable, View, Text, Image } from "react-native";
import Animated, { FadeInRight } from "react-native-reanimated";

import { truncateMiddle } from "@/utils/text/FormatTextUtils";
import { checkIcon } from "@/utils/transactions/ExchangeCurrencies";

export const wallet = new WalletConnectWallet({
  network: WalletConnectChainID.Mainnet,
  options: {
    relayUrl: "wss://relay.walletconnect.com",
    projectId: "373731a9720ac2b80cb2f05467a17898",
    metadata: {
      name: "Izzi Wallet",
      description: "Izzi Wallet WalletConnect",
      url: "https://izziwallet.io/",
      icons: ["https://app.justlend.org/mainLogo.svg"],
    },
  },
  web3ModalConfig: {
    themeMode: "light",
    themeVariables: {
      "--wcm-accent-color": "#5D0CBA",
      "--wcm-background-color": "#5D0CBA",
    },
  },
});

export interface TronWalletSource {
  address: string | null;
}

interface TronWalletProps {
  connectedTronWallet: (source: string | null) => void;
  clearTronWalletAddress: () => void;
  disconnectTronWallet: () => void;
  smallVersion?: boolean;
}

export const checkTronWalletStatus = (): string | null => {
  try {
    //@ts-ignore
    if (window.tronLink?.ready) {
      //@ts-ignore
      return tronLink.tronWeb.defaultAddress.base58;
    }
    return null;
  } catch (error) {
    console.error(error);
  }
};

export const connectTronWallet = async () => {
  try {
    //@ts-ignore
    const res = await window?.tronLink.request({
      method: "tron_requestAccounts",
    });
    if (res.code === 200) {
      return true;
    }
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const disconnectTronWallet = async () => {
  try {
    await wallet.disconnect();
  } catch (error) {
    console.error(error);
  }
};

export const TronWallet = (props: TronWalletProps) => {
  const [isConnected, setIsConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState<string | null>("");
  const { t } = useTranslation();

  useFocusEffect(
    React.useCallback(() => {
      isTronWalletConnected();
    }, [])
  );

  const isTronWalletConnected = async () => {
    try {
      const checkConnectivity = checkTronWalletStatus();
      if (checkConnectivity) {
        setIsConnected(true);
        setWalletAddress(checkConnectivity);
        props.connectedTronWallet(checkConnectivity);
      } else {
        setIsConnected(false);
        props.clearTronWalletAddress();
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Animated.View entering={FadeInRight.delay(100)}>
      {isConnected && walletAddress ? (
        <View className="flex-col w-64 gap-2 p-2 rounded-md bg-gray border-red border items-center">
          <View className="flex-col border-b border-midgray items-center w-full">
            {checkIcon("TRX", 40)}
            <Text
              numberOfLines={1}
              ellipsizeMode="tail"
              className="text-base text-black1 font-light max-w-48"
            >
              {truncateMiddle(walletAddress)}
            </Text>
          </View>
          <View
            className={`flex-row items-center gap-1 ${
              props.smallVersion ? "hidden" : ""
            }`}
          >
            <Text className="font-light text-sm">
              {t("common:terms.connectedwith")}
            </Text>
            <Image
              source={require("@/assets/tronlink-logo.jpeg")}
              style={{ height: 20, width: 20, borderRadius: 4 }}
              resizeMode="contain"
            />
          </View>
          <View className="transition-colors delay-100 items-center bg-purple1 hover:bg-purple3 px-2 py-1 rounded-sm">
            <Pressable
              onPress={async () => {
                await disconnectTronWallet();
                props.disconnectTronWallet();
              }}
            >
              <Text className="text-xs text-white1 font-light">
                {t("common:terms.disconnect")}
              </Text>
            </Pressable>
          </View>
        </View>
      ) : (
        <View className="flex-col items-center gap-2">
          <View className="flex-row gap-1 items-center">
            <Ionicons
              name="alert-circle-outline"
              size={16}
              className="text-darkergray"
            />
            <Text className="text-sm text-darkergray text-center">
              {t("common:terms.tronlinkalert")}
            </Text>
          </View>
          <Pressable
            onPress={async () => {
              //@ts-ignore
              if (window.tronLink?.ready) {
                //@ts-ignore
                const tronLinkWindow = tronLink.tronWeb;
                props.connectedTronWallet(
                  tronLinkWindow.defaultAddress?.base58
                );
                return;
              }
              const request = await connectTronWallet();
              if (request) {
                await isTronWalletConnected();
              }
            }}
            className="bg-purple1 hover:bg-purple3 transition-colors delay-100 rounded-md items-center justify-center p-2 w-56 h-12"
          >
            <Text className="text-base font-medium text-center text-white1">
              {t("common:button.connecttronwallet")}
            </Text>
          </Pressable>
        </View>
      )}
    </Animated.View>
  );
};
